import { MSOPractice } from '../types';

// Helper function to generate deterministic random using seed
const seededRandom = (index: number, salt: number): number => {
  const x = Math.sin(index * salt) * 10000;
  return x - Math.floor(x);
};

// Insurance networks
const insuranceNetworks = [
  'Blue Cross Blue Shield',
  'UnitedHealthcare',
  'Aetna',
  'Cigna',
  'Humana',
  'Medicare',
  'Medicaid',
];

// 9 anonymized markets (Coastal states for ambiguity)
export const microRegions = [
  { id: 'georgia-market-1', name: 'Georgia Market 1', state: 'GA', center: [-84.2, 33.0] as [number, number], targetCount: 7 },
  { id: 'north-carolina-market-1', name: 'North Carolina Market 1', state: 'NC', center: [-80.5, 35.5] as [number, number], targetCount: 9 },
  { id: 'alabama-market-1', name: 'Alabama Market 1', state: 'AL', center: [-87.8, 30.7] as [number, number], targetCount: 9 },
  { id: 'kentucky-market-1', name: 'Kentucky Market 1', state: 'KY', center: [-85.5, 37.8] as [number, number], targetCount: 9 },
  { id: 'alabama-market-2', name: 'Alabama Market 2', state: 'AL', center: [-85.5, 32.6] as [number, number], targetCount: 9 },
  { id: 'florida-market-1', name: 'Florida Market 1', state: 'FL', center: [-85.3, 30.4] as [number, number], targetCount: 9 },
  { id: 'south-carolina-market-1', name: 'South Carolina Market 1', state: 'SC', center: [-79.4, 34.2] as [number, number], targetCount: 6 },
  { id: 'louisiana-market-1', name: 'Louisiana Market 1', state: 'LA', center: [-92.1, 32.5] as [number, number], targetCount: 9 },
  { id: 'mississippi-market-1', name: 'Mississippi Market 1', state: 'MS', center: [-89.0, 34.2] as [number, number], targetCount: 9 },
];

// 76 target practice indices - distributed across markets matching targetCount
// All indices are unique to ensure exactly 76 targets
const TARGET_INDICES = [
  // Georgia Market 1 (7 targets)
  12, 89, 234, 567, 1023, 2456, 4789,
  // North Carolina Market 1 (9 targets)
  34, 156, 345, 678, 1234, 2678, 3456, 5012, 6789,
  // Alabama Market 1 (9 targets)
  67, 189, 456, 789, 1456, 2890, 3678, 5234, 7012,
  // Kentucky Market 1 (9 targets)
  98, 223, 578, 890, 1567, 3012, 3890, 5456, 7234,
  // Alabama Market 2 (9 targets)
  123, 267, 623, 1012, 1678, 3234, 4012, 5678, 7456,
  // Florida Market 1 (9 targets)
  145, 289, 645, 1123, 1890, 3501, 4234, 5890, 7678,
  // South Carolina Market 1 (6 targets)
  178, 312, 712, 1289, 2012, 6012,
  // Louisiana Market 1 (9 targets)
  201, 334, 734, 1345, 2234, 3712, 4456, 6234, 7890,
  // Mississippi Market 1 (9 targets)
  245, 356, 756, 1489, 2501, 3945, 4678, 6456, 8012,
];

/**
 * Target filter distributions for 8,200 practices
 */
const FILTER_TARGETS = {
  specialtyServices: 5400,   // 66% - specialty care
  agingOwner: 2100,          // 26% - aging ownership
  maturePractice: 4900,      // 60% - established 10+ years
  revenueStability: 1640,    // 20% - $1.5M-$7M sweet spot
  insuranceNetworks: 6500,   // 79% - good network participation
  techIntegration: 3280,     // 40% - tech ready
  groupPractice: 2050,       // 25% - multi-physician
};

/**
 * Calculate filter flags deterministically
 */
const calculateMSOFilterFlags = (index: number, totalPractices: number, isTarget: boolean) => {
  const meetsCriterion = (targetCount: number, salt: number, correlation: number = 0): boolean => {
    if (isTarget) return true; // All targets meet all criteria
    const baseThreshold = targetCount / totalPractices;
    const random = seededRandom(index, salt) + correlation;
    return random < baseThreshold;
  };

  const specialtyServices = meetsCriterion(FILTER_TARGETS.specialtyServices, 2001);
  const agingOwner = meetsCriterion(FILTER_TARGETS.agingOwner, 3002);
  const maturePractice = meetsCriterion(FILTER_TARGETS.maturePractice, 4003, agingOwner ? 0.15 : 0);
  const revenueStability = meetsCriterion(FILTER_TARGETS.revenueStability, 5004);
  const insuranceNetworks = meetsCriterion(FILTER_TARGETS.insuranceNetworks, 6005);
  const techIntegration = meetsCriterion(FILTER_TARGETS.techIntegration, 7006);
  const groupPractice = meetsCriterion(FILTER_TARGETS.groupPractice, 8007, maturePractice ? 0.1 : 0);

  return {
    specialtyServices,
    agingOwner,
    maturePractice,
    revenueStability,
    insuranceNetworks,
    techIntegration,
    groupPractice,
  };
};

/**
 * Generate a single practice
 */
const generatePractice = (index: number, totalPractices: number): MSOPractice => {
  // Determine if this is a target practice
  const isTarget = TARGET_INDICES.includes(index);

  // Assign to market (distribute evenly)
  const marketIndex = Math.floor((index - 1) / (totalPractices / microRegions.length)) % microRegions.length;
  const market = microRegions[marketIndex];

  // Deterministic specialty assignment
  // For targets: 34% primary care (26 out of 76), 66% specialty (50 out of 76)
  // For overall: similar mix
  let specialty: MSOPractice['specialty'];
  const targetPrimaryCareIndices = TARGET_INDICES.slice(0, 26); // First 26 targets are primary care

  if (isTarget) {
    if (targetPrimaryCareIndices.includes(index)) {
      specialty = 'primary-care';
    } else {
      const specialtyOptions: ('cardiology' | 'dermatology' | 'orthopedics' | 'gastroenterology')[] =
        ['cardiology', 'dermatology', 'orthopedics', 'gastroenterology'];
      specialty = specialtyOptions[index % 4];
    }
  } else {
    const isPrimaryCare = seededRandom(index, 1234) < 0.34;
    if (isPrimaryCare) {
      specialty = 'primary-care';
    } else {
      const specialtyOptions: ('cardiology' | 'dermatology' | 'orthopedics' | 'gastroenterology')[] =
        ['cardiology', 'dermatology', 'orthopedics', 'gastroenterology'];
      const specialtyIndex = Math.floor(seededRandom(index, 5678) * 4);
      specialty = specialtyOptions[specialtyIndex];
    }
  }

  // Generate revenue (more in target range for targets)
  const revenueBase = seededRandom(index, 9012);
  const revenue = isTarget
    ? 1.5 + (revenueBase * 5.5) // $1.5M - $7M for targets
    : 0.5 + (revenueBase * 12); // $0.5M - $12.5M for others

  // Owner age
  const ownerAge = isTarget
    ? 60 + Math.floor(seededRandom(index, 1112) * 11) // 60-70 for targets
    : 45 + Math.floor(seededRandom(index, 1113) * 30); // 45-75 for others

  // Years established
  const yearsEstablished = isTarget
    ? 10 + Math.floor(seededRandom(index, 1314) * 40) // 10-50 years for targets
    : 1 + Math.floor(seededRandom(index, 1315) * 60); // 1-61 years for others

  // Tech readiness
  const techRand = seededRandom(index, 1516);
  const techReadiness: 'low' | 'medium' | 'high' = isTarget
    ? (techRand < 0.5 ? 'medium' : 'high')
    : (techRand < 0.33 ? 'low' : techRand < 0.66 ? 'medium' : 'high');

  // Insurance networks
  const numNetworks = isTarget
    ? 4 + Math.floor(seededRandom(index, 1718) * 3) // 4-6 networks for targets
    : 1 + Math.floor(seededRandom(index, 1719) * 6); // 1-6 networks for others

  const selectedNetworks: string[] = [];
  for (let i = 0; i < numNetworks; i++) {
    const networkIndex = Math.floor(seededRandom(index, 2000 + i) * insuranceNetworks.length);
    if (!selectedNetworks.includes(insuranceNetworks[networkIndex])) {
      selectedNetworks.push(insuranceNetworks[networkIndex]);
    }
  }

  // Random coordinates near market center
  const offsetLat = (seededRandom(index, 3001) - 0.5) * 0.3;
  const offsetLon = (seededRandom(index, 3002) - 0.5) * 0.3;

  // Calculate filter flags
  const filterFlags = calculateMSOFilterFlags(index, totalPractices, isTarget);

  return {
    id: `practice-${index}`,
    name: `Practice M-${index}`,
    specialty,
    revenue: parseFloat(revenue.toFixed(2)),
    location: {
      region: market.name,
      state: market.state,
      coordinates: [
        market.center[0] + offsetLon,
        market.center[1] + offsetLat,
      ],
    },
    characteristics: {
      ownerAge,
      yearsEstablished,
      insuranceNetworks: selectedNetworks,
      techReadiness,
    },
    clusterAssignment: market.id,
    priorityScore: isTarget ? 85 + Math.floor(seededRandom(index, 4001) * 15) : Math.floor(seededRandom(index, 4002) * 100),
    isTarget,
    filterFlags,
  };
};

/**
 * Generate all 8,200 practices
 */
export const generateMSOPractices = (): MSOPractice[] => {
  const totalPractices = 8200;
  const practices: MSOPractice[] = [];

  for (let i = 1; i <= totalPractices; i++) {
    practices.push(generatePractice(i, totalPractices));
  }

  return practices;
};

// Export practices
export const msoPractices = generateMSOPractices();

// Export sample for testing
export const samplePractices = msoPractices.slice(0, 30);
